import { isValid } from "date-fns";
import { useId, useState, type Ref, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { Event } from "@/api/bale.zod";

import { useCreateEvent } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { CardHeader, CardTextContent, CardTitle } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { EndTimeInput } from "@/components/EndTimeInput";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { Input } from "@/components/Input";
import { RichTextarea } from "@/components/RichTextarea";
import { StartTimeInput } from "@/components/StartTimeInput";
import { Subtitle } from "@/components/Subtitle";
import { TimeSpanInputProvider } from "@/components/TimeSpanInputProvider";
import { htmlDate } from "@/fp/htmlDate";
import { useCurrentTime } from "@/hooks/useCurrentTime";

export function CreateEventModal({
  ref,
  template,
  onClose,
}: {
  template?: Event;
  ref: Ref<HTMLDialogElement>;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createEvent = useCreateEvent({
    mutation: {
      onSuccess: (response) => navigate(`/event/${response.data.event.id}`),
    },
  });
  const now = useCurrentTime("@secondly");
  const [endTime, setEndTime] = useState<Date | null>(null);
  const endTimeWarning = endTime && isValid(endTime) && endTime < now ? t("this-time-has-passed") : null;

  function handleSubmit(e: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(e.currentTarget);
    const name = form.get("name")! as string;
    const description = form.get("description")! as string;
    const start_time = form.get("start_time")! as string;
    const end_time = form.get("end_time")! as string;
    const location = form.get("location")! as string;
    createEvent.mutate({
      data: {
        template_id: template?.id ?? null,
        name,
        location,
        description,
        start_time: start_time ? new Date(start_time) : null,
        end_time: end_time ? new Date(end_time) : null,

        // TODO: support these fields in this modal
        max_attendees: null,
        discoverable_distance: null,
      },
    });
  }

  const labelId = useId();

  return (
    <Dialog ref={ref} className="w-lg" aria-labelledby={labelId}>
      <CardHeader>
        <CardTitle>
          <Subtitle id={labelId}>{t("create-event")}</Subtitle>
        </CardTitle>
      </CardHeader>
      <CardTextContent>
        <Form
          error={createEvent.error}
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          aria-labelledby={labelId}
        >
          <FormField title={t("event-name")}>
            <Input
              name="name"
              placeholder={t("event-name")}
              defaultValue={template?.name}
              disabled={createEvent.isPending}
            />
          </FormField>
          <FormField title={t("event-description")}>
            <RichTextarea
              name="description"
              defaultValue={template?.description.raw ?? ""}
              placeholder={t("event-description-placeholder")}
              disabled={createEvent.isPending}
            />
          </FormField>
          <TimeSpanInputProvider>
            <FormField title={t("event-start")}>
              <StartTimeInput
                name="start_time"
                defaultValue={template?.start_time ? htmlDate(template.start_time) : ""}
                disabled={createEvent.isPending}
              />
            </FormField>
            <FormField
              title={
                <div className="flex flex-row">
                  {t("event-end")}
                  {endTimeWarning && <span className="ml-auto text-danger">{endTimeWarning}</span>}
                </div>
              }
            >
              <EndTimeInput
                name="end_time"
                defaultValue={template?.end_time ? htmlDate(template.end_time) : ""}
                onChange={(event) => setEndTime(new Date(event.currentTarget.value))}
                disabled={createEvent.isPending}
              />
            </FormField>
          </TimeSpanInputProvider>
          <FormField title={t("event-where")}>
            <Input
              type="text"
              name="location"
              defaultValue={template?.location ?? ""}
              placeholder={t("event-where-placeholder")}
              disabled={createEvent.isPending}
            />
          </FormField>
          <ActionBox>
            <Button type="reset" attention="danger" onClick={onClose} disabled={createEvent.isPending}>
              {t("cancel")}
            </Button>
            <Button type="submit" attention="submit" disabled={createEvent.isPending}>
              {createEvent.isPending ? t("creating-event") : t("create-event")}
            </Button>
          </ActionBox>
        </Form>
      </CardTextContent>
    </Dialog>
  );
}
